import { ColorScheme } from "../../../.storyblok/types/287059/storyblok-components";
import { useImageLoading } from "../../helpers/ImageLoadingContext";
import { analyticsFilter } from "../../helpers/analytics";
import { fallbackColorScheme } from "../../helpers/colorSchemes";
import { FlyOutData } from "../../helpers/data/getFlyOutData";
import { getImageSource } from "../../helpers/getImageSource";
import { getStoryblokImageUrl } from "../../helpers/images/getStoryblokImageUrl";
import { useMediaQuery } from "../../helpers/useMediaQuery";
import { ProductSquare } from "../ProductSquare/ProductSquare";
import { CosmosButton, CosmosIconClose, CosmosTitle } from "@cosmos/web-react";
import classNames from "classnames/bind";
import styles from "./RailFlyOver.module.css";

const cx = classNames.bind(styles);

export function RailFlyOver({
  title,
  categories,
  featuredProducts,
  onClose,
}: {
  title: string;
  featuredProducts: FlyOutData["featuredProducts"];
  categories: FlyOutData["categories"];
  onClose: () => void;
}) {
  const isDesktop = useMediaQuery("(width >= 650px)", {
    initializeWithValue: false,
  });

  return (
    <div className={cx("container")}>
      <div className={cx("header")}>
        <CosmosTitle appearance="dark" size="medium" className={cx("title")}>
          {title}
        </CosmosTitle>
        <CosmosButton
          onClick={onClose}
          className={cx("close-button")}
          size={isDesktop ? "large" : "small"}
          appearance="dark"
          kind="secondary"
          shape="circle"
          aria-label="Close"
        >
          <CosmosIconClose className={cx("icon-close")} slot="icon" />
        </CosmosButton>
      </div>
      <div className={cx("inner")}>
        <div className={cx("featuredProducts")}>
          {featuredProducts.map((product, index) => (
            <FeaturedProduct
              key={index}
              title={product.title}
              graphicUrl={product.graphicUrl}
              graphicAlt={product.graphicAlt}
              productId={product.productId}
              colorScheme={product.colorScheme}
              url={product.url}
            />
          ))}
        </div>
        {categories.map((category, index) => (
          <ProductsCategory
            key={index}
            title={category.title}
            products={category.products}
            url={category.url}
          />
        ))}
      </div>
    </div>
  );
}

function FeaturedProduct({
  productId,
  colorScheme = fallbackColorScheme,
  graphicUrl,
  graphicAlt,
  url,
  title,
}: {
  productId: string;
  title: string;
  colorScheme: ColorScheme | undefined;
  graphicUrl: string;
  graphicAlt?: string;
  url: string;
}) {
  const backgroundStyle = {
    "--productBackgroundColor": colorScheme.secondaryColor,
  };
  const imageSource = graphicUrl ? getImageSource(graphicUrl) : null;

  const imageLoading = useImageLoading();

  return (
    <a
      className={cx("featuredProduct")}
      href={url}
      style={backgroundStyle}
      onClick={() => {
        analyticsFilter({
          value: productId,
          contentType: "range",
        });
      }}
    >
      <div className={cx("featuredProduct__inner")}>
        <CosmosTitle
          className={cx("title")}
          appearance={colorScheme.textAppearance}
          size="small"
        >
          {title}
        </CosmosTitle>
        {imageSource === "storyblok" ? (
          <img
            className={cx("featuredProduct__image")}
            loading={imageLoading}
            src={getStoryblokImageUrl(graphicUrl, {
              width: 352,
              height: undefined,
            })}
            alt={graphicAlt}
          />
        ) : (
          <img
            style={backgroundStyle}
            className={cx("featuredProduct__image")}
            loading={imageLoading}
            src={graphicUrl}
            alt={graphicAlt}
          />
        )}
      </div>
    </a>
  );
}

const MaybeLink = ({
  children,
  href,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement>) =>
  href && href !== "#" ? (
    <a href={href} {...rest}>
      {children}
    </a>
  ) : (
    children
  );

function ProductsCategory({
  title,
  url,
  products,
}: {
  title: string;
  url: string | undefined;
  products: {
    colorScheme: ColorScheme | undefined;
    graphicUrl: string;
    grahicAlt?: string;
    url: string;
    productId: string;
  }[];
}) {
  return (
    <div>
      <MaybeLink
        className={cx("productsCategory")}
        /**
         * If these links are empty strings (which can happen if there's no
         * category page in this locale) then we don't want the wrapping link.
         */
        href={url}
        onClick={() => {
          analyticsFilter({ value: title, contentType: "range" });
        }}
      >
        <CosmosTitle appearance="dark" size="x-small">
          {title}
        </CosmosTitle>
      </MaybeLink>
      <div className={cx("productsSection")}>
        {products.map((product, index) => {
          return (
            <a
              key={index}
              className={cx("productSquare")}
              href={product.url}
              onClick={() =>
                analyticsFilter({
                  value: product.productId,
                  contentType: "range",
                })
              }
            >
              <ProductSquare
                imageAlt={product.grahicAlt}
                imageUrl={product.graphicUrl}
                colorScheme={product.colorScheme ?? fallbackColorScheme}
              />
            </a>
          );
        })}
      </div>
    </div>
  );
}
